/**
 * @fileoverview Invoice cancellation modal component
 * Allows users to cancel checkout invoices with a reason
 */
import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography, TextField, Alert } from "@mui/material";
import { DmsModal, DmsButton, intl, fetchData, HTTP_STATUS } from "dmsbasereact";
import Swal from "sweetalert2";

/**
 * Modal for cancelling invoice documents
 * @param {boolean} open - Whether the modal is open
 * @param {Function} onClose - Callback when modal is closed
 * @param {Object} invoice - The invoice to cancel
 * @param {Function} onSuccess - Callback when cancellation is successful
 */
export const InvoiceCancelModal = ({ open, onClose, invoice, onSuccess }) => {
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);

    const handleClose = () => {
        setReason("");
        onClose();
    };

    const handleConfirm = async () => {
        if (!invoice || reason.trim().length < 10) {
            Swal.fire({
                icon: "warning",
                title: intl.get("ReasonRequired").d("Reason Required"),
                text: intl.get("CancellationReasonMinLength").d("Cancellation reason must be at least 10 characters."),
            });
            return;
        }

        try {
            setLoading(true);
            const response = await fetchData(
                `/invoice_document/${invoice.pk}/cancel`,
                "POST",
                true,
                { reason: reason.trim() }
            );

            if (response.status === HTTP_STATUS.OK_200) {
                Swal.fire({
                    icon: "success",
                    title: intl.get("InvoiceCancelled").d("Invoice Cancelled"),
                    text: intl.get("InvoiceCancelledSuccess").d("The invoice has been cancelled and stock has been reversed."),
                });
                handleClose();
                if (onSuccess) onSuccess();
            } else {
                throw new Error(response.data?.detail || "Failed to cancel invoice");
            }
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: err.response?.data?.detail || err.message || intl.get("FailedToCancelInvoice").d("Failed to cancel invoice"),
            });
        } finally {
            setLoading(false);
        }
    };

    if (!invoice) return null;

    const itemsCount = invoice.invoice_document_items?.length || 0;
    const totalQuantity = invoice.invoice_document_items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

    return (
        <DmsModal
            isVisible={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DmsModal.Header title={intl.get("CancelInvoice").d("Cancel Invoice")} />
            <DmsModal.Body>
                <Box sx={{ p: 2 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {intl.get("StockReversalWarning").d("Stock will be automatically reversed to the origin.")}
                    </Alert>

                    <Box sx={{ mb: 2, p: 2, bgcolor: "background.default", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{intl.get("InvoiceNumber").d("Invoice Number")}:</strong> {invoice.danfe_number}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{intl.get("OwnerEntity").d("Owner Entity")}:</strong> {invoice.owner_entity?.name || "-"}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{intl.get("DestinationEntity").d("Destination Entity")}:</strong> {invoice.destination_entity?.name || "-"}
                        </Typography>
                        <Typography variant="body2">
                            <strong>{intl.get("Items").d("Items")}:</strong> {itemsCount} ({totalQuantity} {intl.get("Units").d("units")})
                        </Typography>
                    </Box>

                    <Typography variant="body2" gutterBottom>
                        {intl.get("CancellationReasonPrompt").d("Please provide a reason for cancellation (minimum 10 characters):")}
                    </Typography>

                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={intl.get("EnterCancellationReason").d("Enter cancellation reason...")}
                        inputProps={{ maxLength: 500 }}
                        error={reason.length > 0 && reason.length < 10}
                        helperText={
                            reason.length > 0 && reason.length < 10
                                ? intl.get("MinCharsRequired", { min: 10 }).d("Minimum 10 characters required")
                                : ""
                        }
                    />
                </Box>
            </DmsModal.Body>
            <DmsModal.Footer>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
                    <DmsButton
                        variant="secondary"
                        text={intl.get("Cancel").d("Cancel")}
                        onClick={handleClose}
                        disabled={loading}
                    />
                    <DmsButton
                        variant="danger"
                        text={intl.get("ConfirmCancellation").d("Confirm Cancellation")}
                        onClick={handleConfirm}
                        disabled={loading || reason.trim().length < 10}
                    />
                </Box>
            </DmsModal.Footer>
        </DmsModal>
    );
};

InvoiceCancelModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    invoice: PropTypes.shape({
        pk: PropTypes.number.isRequired,
        danfe_number: PropTypes.string,
        owner_entity: PropTypes.shape({
            name: PropTypes.string,
        }),
        destination_entity: PropTypes.shape({
            name: PropTypes.string,
        }),
        invoice_document_items: PropTypes.arrayOf(
            PropTypes.shape({
                quantity: PropTypes.number,
            })
        ),
    }),
    onSuccess: PropTypes.func,
};
